import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash, Wallet, BookOpen, Scales } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { money, formatApiErrorDetail } from "../lib/api";
import { SectionHeader, StatusChip, Money, useFocusHighlight } from "../components/shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { DataTable } from "../components/DataTable";
import { usePerms } from "../lib/perms";

const ACCOUNT_TYPES = ["Asset", "Liability", "Equity", "Income", "Expense"];
const TYPE_COLOR = {
  Asset: "text-blue-700", Liability: "text-amber-700", Equity: "text-purple-700",
  Income: "text-emerald-700", Expense: "text-red-700",
};

export default function Finance() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "accounts";
  const setTab = (v) => { const p = new URLSearchParams(searchParams); p.set("tab", v); setSearchParams(p, { replace: true }); };
  return (
    <div className="animate-fade-up">
      <Tabs value={tab} onValueChange={setTab}>
        <TabsContent value="accounts"><Accounts /></TabsContent>
        <TabsContent value="journals"><Journals /></TabsContent>
      </Tabs>
    </div>
  );
}

function Accounts() {
  const qc = useQueryClient();
  const { rowProps } = useFocusHighlight();
  const { can } = usePerms();
  const EMPTY = { code: "", name: "", type: "Asset", balance: 0 };
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const { data: accounts = [] } = useQuery({ queryKey: ["accounts"], queryFn: async () => (await api.get("/accounts")).data });

  const save = useMutation({
    mutationFn: async () => {
      const payload = { ...form, balance: Number(form.balance) };
      if (editId) return (await api.put(`/accounts/${editId}`, payload)).data;
      return (await api.post("/accounts", payload)).data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["accounts"] }); setOpen(false); setForm(EMPTY); setEditId(null); toast.success("Account saved"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const del = useMutation({
    mutationFn: async (id) => api.delete(`/accounts/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["accounts"] }); toast.success("Account deleted"); },
  });

  return (
    <>
      <SectionHeader title="Chart of Accounts" subtitle="General ledger">
        {can("finance.edit") && <Button onClick={() => { setForm(EMPTY); setEditId(null); setOpen(true); }} data-testid="new-account-button" className="rounded-sm bg-primary hover:bg-primary/90 h-9"><Plus size={16} weight="bold" className="mr-1" /> New Account</Button>}
      </SectionHeader>
      <DataTable
        testid="accounts-table"
        data={accounts}
        rowKey={(a) => a.id}
        rowTestid="account-row"
        rowProps={(a) => rowProps(a.id)}
        empty="No accounts yet."
        columns={[
          { key: "code", header: "Code", filter: "text", cellClassName: "font-mono text-xs font-medium" },
          { key: "name", header: "Account Name", filter: "text", cellClassName: "text-sm font-medium" },
          { key: "type", header: "Type", filter: "select", options: ACCOUNT_TYPES, cell: (a) => <span className={`text-sm font-semibold ${TYPE_COLOR[a.type]}`}>{a.type}</span> },
          { key: "balance", header: "Balance", align: "right", numeric: true, cell: (a) => <Money value={a.balance} className="text-sm font-medium" /> },
          { key: "actions", header: "", sortable: false, align: "right", cell: (a) => (
            <div className="flex justify-end gap-1">
              {can("finance.edit") && <button onClick={() => { setForm(a); setEditId(a.id); setOpen(true); }} className="p-1.5 text-muted-foreground hover:text-primary" data-testid="edit-account"><BookOpen size={16} /></button>}
              {can("finance.delete") && <button onClick={() => del.mutate(a.id)} className="p-1.5 text-muted-foreground hover:text-destructive" data-testid="delete-account"><Trash size={16} /></button>}
            </div>
          ) },
        ]}
      />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Wallet size={20} className="text-primary" /> {editId ? "Edit" : "New"} Account</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-3 gap-4">
              <div><Label className="overline-label">Code</Label><Input data-testid="account-code-input" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="mt-1 rounded-sm font-mono" /></div>
              <div className="col-span-2"><Label className="overline-label">Name</Label><Input data-testid="account-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-sm" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="overline-label">Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="account-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{ACCOUNT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="overline-label">Opening Balance</Label><Input type="number" value={form.balance} onChange={(e) => setForm({ ...form, balance: e.target.value })} className="mt-1 rounded-sm num" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={() => save.mutate()} disabled={!form.code || !form.name || save.isPending} data-testid="save-account-button" className="rounded-sm bg-primary hover:bg-primary/90">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Journals() {
  const qc = useQueryClient();
  const { can } = usePerms();
  const today = new Date().toISOString().slice(0, 10);
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(today);
  const [description, setDescription] = useState("");
  const [lines, setLines] = useState([
    { account_id: "", debit: 0, credit: 0 },
    { account_id: "", debit: 0, credit: 0 },
  ]);

  const { data: entries = [] } = useQuery({ queryKey: ["journal-entries"], queryFn: async () => (await api.get("/journal-entries")).data });
  const { data: accounts = [] } = useQuery({ queryKey: ["accounts"], queryFn: async () => (await api.get("/accounts")).data });

  const totalDebit = lines.reduce((s, l) => s + Number(l.debit || 0), 0);
  const totalCredit = lines.reduce((s, l) => s + Number(l.credit || 0), 0);
  const balanced = Math.abs(totalDebit - totalCredit) < 0.01 && totalDebit > 0;

  const reset = () => { setDate(today); setDescription(""); setLines([{ account_id: "", debit: 0, credit: 0 }, { account_id: "", debit: 0, credit: 0 }]); };

  const create = useMutation({
    mutationFn: async () => {
      const payload = {
        date, description, status: "posted",
        lines: lines.filter((l) => l.account_id).map((l) => ({
          account_id: l.account_id, account_name: accounts.find((a) => a.id === l.account_id)?.name || "",
          debit: Number(l.debit || 0), credit: Number(l.credit || 0),
        })),
      };
      return (await api.post("/journal-entries", payload)).data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["journal-entries"] }); qc.invalidateQueries({ queryKey: ["accounts"] }); setOpen(false); reset(); toast.success("Journal entry posted"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const del = useMutation({
    mutationFn: async (id) => api.delete(`/journal-entries/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["journal-entries"] }); toast.success("Deleted"); },
  });

  return (
    <>
      <SectionHeader title="Journal Entries" subtitle="Double-entry postings">
        {can("finance.post") && <Button onClick={() => { reset(); setOpen(true); }} data-testid="new-journal-button" className="rounded-sm bg-primary hover:bg-primary/90 h-9"><Plus size={16} weight="bold" className="mr-1" /> New Entry</Button>}
      </SectionHeader>
      <DataTable
        testid="journals-table"
        data={entries}
        rowKey={(e) => e.id}
        rowTestid="journal-row"
        empty="No journal entries yet."
        columns={[
          { key: "entry_no", header: "Entry No.", filter: "text", cellClassName: "font-mono text-xs font-medium" },
          { key: "date", header: "Date", filter: "text", cellClassName: "text-sm text-muted-foreground font-mono" },
          { key: "description", header: "Description", filter: "text", cellClassName: "text-sm", cell: (e) => e.description || "—" },
          { key: "status", header: "Status", filter: "text", cell: (e) => <StatusChip status={e.status} /> },
          { key: "total_debit", header: "Amount", align: "right", numeric: true, cell: (e) => <Money value={e.total_debit} className="text-sm font-medium" /> },
          { key: "actions", header: "", sortable: false, align: "right", cell: (e) => can("finance.delete") ? <button onClick={() => del.mutate(e.id)} className="p-1.5 text-muted-foreground hover:text-destructive" data-testid="delete-journal"><Trash size={16} /></button> : null },
        ]}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Scales size={20} className="text-primary" /> New Journal Entry</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-3 gap-4">
              <div><Label className="overline-label">Date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1 rounded-sm" /></div>
              <div className="col-span-2"><Label className="overline-label">Description</Label><Input value={description} onChange={(e) => setDescription(e.target.value)} className="mt-1 rounded-sm" placeholder="e.g. Monthly rent" /></div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="overline-label">Lines</Label>
                <button onClick={() => setLines([...lines, { account_id: "", debit: 0, credit: 0 }])} data-testid="add-journal-line" className="text-primary text-xs font-semibold flex items-center gap-1 hover:underline"><Plus size={14} /> Add line</button>
              </div>
              <div className="grid grid-cols-12 gap-2 mb-1 px-1">
                <span className="col-span-6 overline-label">Account</span>
                <span className="col-span-3 overline-label text-right">Debit</span>
                <span className="col-span-3 overline-label text-right">Credit</span>
              </div>
              <div className="space-y-2">
                {lines.map((l, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                    <div className="col-span-6">
                      <Select value={l.account_id} onValueChange={(v) => { const c = [...lines]; c[idx].account_id = v; setLines(c); }}>
                        <SelectTrigger className="rounded-sm h-9 text-sm" data-testid="journal-account-select"><SelectValue placeholder="Select account" /></SelectTrigger>
                        <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.code} · {a.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <Input className="col-span-3 rounded-sm h-9 num" type="number" value={l.debit} onChange={(e) => { const c = [...lines]; c[idx].debit = e.target.value; c[idx].credit = 0; setLines(c); }} />
                    <Input className="col-span-3 rounded-sm h-9 num" type="number" value={l.credit} onChange={(e) => { const c = [...lines]; c[idx].credit = e.target.value; c[idx].debit = 0; setLines(c); }} />
                  </div>
                ))}
              </div>
            </div>
            <div className="border-t border-border pt-3 flex items-center justify-between text-sm">
              <span className={`font-semibold ${balanced ? "text-emerald-600" : "text-red-600"}`}>
                {balanced ? "Balanced ✓" : "Out of balance"}
              </span>
              <div className="flex gap-6">
                <span>Debit: <strong className="tabular">{money(totalDebit)}</strong></span>
                <span>Credit: <strong className="tabular">{money(totalCredit)}</strong></span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={() => create.mutate()} disabled={!balanced || create.isPending} data-testid="post-journal-button" className="rounded-sm bg-primary hover:bg-primary/90">{create.isPending ? "Posting…" : "Post Entry"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
